/**
 * 工具函数
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-12 09:21:28
 */
import areas from '@/assets/hangzhou.json';
/**
 * 根据行政编码获取省市区名称
 * 同步版本
 * @author  Jerry <superzcj_001@163.com>
 * @since   2020-09-02 23:36:08
 */
export const getAddressSync = (provance, city, region, delimiter = ' ') => {
    const data = areas;
    const ret = [];
    const provanceNode = data.find(el => el.value === provance);
    if (provanceNode !== null) {
        ret.push(provanceNode);
        const cityNode = provanceNode?.children.find(el => el.value === city);
        if (cityNode !== null) {
            ret.push(cityNode);
            const regionNode = cityNode?.children.find(el => el.value === region);
            if (cityNode !== null) {
                ret.push(regionNode);
            }
        }
    }
    return ret.map(el => el?.label).join(delimiter);
};
const flatCity = (area) => {
    const { children, ...rest } = area;
    return [{ ...rest, children: [] }, ...children];
};
/**
 * 获取单个地区
 * @author  Jerry <superzcj_001@163.com>
 * @since   2020-09-23 11:18:43
 */
export const getCitySync = (code) => {
    const _code = typeof code === 'string' ? parseInt(code, 10) : code;
    const data = areas.flatMap(flatCity).flatMap(flatCity);
    return data.find(area => area.id === _code) ?? { label: _code };
};
/**
 * 将Vue响应式对象转换成一个新的干净的对象
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-12 09:23:29
 * @param {any} obj 待转换的Vue响应式对象
 * @return {any} cleanObj 干净的对象
 */
export const getCleanObjFromVue = (obj) => JSON.parse(JSON.stringify(obj));
// the shorter and no any type version
export const parse = (obj) => JSON.parse(JSON.stringify(obj));
/**
 * 计算以给定二维数组第一维元素为因子的笛卡尔积
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-15 15:25:40
 */
export const TowDArray2CartesianProduct = (arr) => {
    if (arr.length === 1) {
        return arr[0].map(el => [el]);
    }
    const ret = arr.reduce((acc, cur) => {
        if (acc.length === 0) {
            return cur.map(curEl => [curEl]);
        }
        const res = [];
        acc.forEach((accEl) => {
            cur.forEach((curEl) => {
                const accArr = Array.isArray(accEl) ? accEl : [accEl];
                res.push([...accArr, curEl]);
            });
        });
        return res;
    }, []);
    return ret;
};
// date/time
/**
 * 简单的日期时间格式化，根据商品列表UI来的
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-18 11:17:47
 * @param {number} timestamp 时间戳(毫秒)
 */
export const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return ([date.getFullYear(), date.getMonth() + 1, date.getDate()]
        .map(el => `${el}`.padStart(2, '0'))
        .join('.') +
        ' ' +
        [date.getHours(), date.getMinutes(), date.getSeconds()]
            .map(el => `${el}`.padStart(2, '0'))
            .join(':'));
};
/**
 * 判断是否空数组
 * @param {any} val
 * @return {boolean}
 */
export const isEmptyArray = (val) => Array.isArray(val) && val.length === 0;
/**
 * 判断是否对象(非数组)
 * @param {any} val
 * @return {boolean}
 */
export const isObject = (val) => typeof val === 'object' && !Array.isArray(val);
/**
 * 判断是否空对象(非数组)
 * @param {any} val
 * @return {boolean}
 */
export const isEmptyObject = (val) => isObject(val) && Object.keys(val).length === 0;
/**
 * 判断变量是否为空值
 * 空字符串、空数组、空对象、undefined、null 均判定为空值
 */
export const isEmpty = (val) => typeof val === 'number'
    ? false
    : val === '' || val === undefined || val === null || isEmptyArray(val) || isEmptyObject(val);
/**
 * 类似 Array.prototype.map 的方式
 * 将一个非数组对象进行映射到新的对象
 * @param {object} obj 待转换的对象
 * @param fn 映射函数
 * @return {object} newObj 新的对象
 *
 * ```
 *  const obj = { a: 1, b: 2 };
 *  const newObj = ObjectMap(obj, ([ key, val ]) => ([ key, val * 2 ]));
 *  // Result: { a: 2, b: 4 }
 * ```
 */
export const ObjectMap = (obj, fn) => Object.fromEntries(Object.entries(obj).map(fn));
/**
 * 类似 Array.prototype.filter 的方式
 * 将一个非数组对象的特定键值对过滤掉
 * @param {object} obj 待转换的对象
 * @param fn 过滤函数
 * @return {object} newObj 新的对象
 */
export const ObjectFilter = (obj, fn) => Object.fromEntries(Object.entries(obj).filter(fn));
/**
 * 过滤掉对象中空值
 * 返回新对象
 * @param {any} obj
 * @return {any} newObj
 */
export const ObjectFilterEmpty = (obj) => ObjectFilter(obj, ([_, value]) => !isEmpty(value));
/**
 * 扁平数组转树
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-20 17:13:28
 */
export function toTree(flatArray, pid = 0) {
    const tree = flatArray
        .filter(node => node.pid === pid)
        .map(node => {
        const children = [];
        return {
            ...node,
            children,
        };
    });
    const leftNode = flatArray.filter(node => node.pid !== pid);
    if (leftNode.length > 0) {
        tree.forEach(node => {
            node.children = [...(node.children ?? []), ...toTree(leftNode, node.id)];
        });
    }
    return tree;
}
/**
 * key -> value object 转 url search params
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-23 14:16:16
 */
export function URLSearchMaker(params) {
    const us = new URLSearchParams(Object.entries(params));
    return us.toString();
}
/**
 * 去重
 * 简单数据类型，不包括对象类型
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-07-01 16:11:33
 */
export const unique = (list) => Array.from(new Set(list));
/**
 * 判断给定数据是否存在重复元素
 * 简单数据类型，不包括对象类型
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-07-01 16:12:35
 */
export const isUnique = (list) => list.length > unique(list).length;
export const parseXML = (domStr) => new DOMParser().parseFromString(domStr, 'text/xml');
/**
 * 从对象中找到指定key对应的val
 * 其实是个没用的函数
 * 但是为了防止Ts类型推导不正确报错
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-07-07 00:52:02
 */
export const ObjectFind = (obj, prop) => {
    const [_, v] = Object.entries(obj).find(([k, _]) => k === prop) ?? [];
    return v;
};
/**
 * 日期转时间戳(秒)
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-07-31 14:33:16
 * @param {Date} date
 * @return {number} timestamp (秒)
 */
export const date2timestamp = (date) => Math.floor(date.getTime() / 1000);
/**
 * 将数据转为完整4个数值的数组
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-07-31 18:29:50
 */
export const getStyleNumbers = (num) => {
    if (!Array.isArray(num)) {
        return [num, num, num, num];
    }
    else if (num.length === 1) {
        const [top] = num;
        return [top, top, top, top];
    }
    else if (num.length === 2) {
        const [top, right] = num;
        return [top, right, top, right];
    }
    else if (num.length === 3) {
        const [top, right, bottom] = num;
        return [top, right, bottom, right];
    }
    else {
        const [top, right, bottom, left] = num;
        return [top, right, bottom, left];
    }
};
// 给数值附加px
export const addCssUnit = (num) => (/^\d+$/.test(`${num}`) ? `${num}px` : `${num}`);
/**
 * 基于偏移量获取字符，基准字符 a
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-05 17:48:47
 * @param {number} offset
 * @return {string}
 */
export const fromCharCode = (offset) => String.fromCharCode('a'.charCodeAt(0) + offset);
/**
 * 基于两个偏移量生成两个字符的字符串 {charA}{charB}
 * 如 aa ab ba bb
 * 主要用于自动生成文件碎片后缀
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-05 17:49:17
 * @param {number} left
 * @param {number} right
 */
export const getSuffix = (left, right) => `${fromCharCode(left)}${fromCharCode(right)}`;
/**
 * 获取碎片文件名列表
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-05 17:50:54
 * @param {number} count 碎片文件数量
 * @param {number} prefix 文件共同前缀
 * @return {StringArray}
 */
export const getFragmentNames = (count) => (prefix) => new Array(count).fill(prefix).map((prefix, index) => {
    const right = index % 26;
    const left = (index - right) / 26;
    return `${prefix}.${getSuffix(left, right)}`;
});
/**
 * 获取服务器碎片文件并合并
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-05 18:47:07
 */
export const fetchFragments = async (count, prefix) => Promise.all(getFragmentNames(count)(prefix).map(url => fetch(url).then(res => res.text()))).then((fragments) => fragments.join(''));
/**
 * 从 blob 数据下载文件
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-07 11:25:47
 * @param {string} filename 文件名称
 * @param {Blob} blob 数据
 */
export const downloadFileFromBlob = (filename, blob) => {
    const elink = document.createElement('a');
    elink.download = filename;
    elink.style.display = 'none';
    elink.href = URL.createObjectURL(blob);
    document.body.appendChild(elink);
    elink.click();
    URL.revokeObjectURL(elink.href);
    document.body.removeChild(elink);
};
/**
 * 将参数数组编码成 base64 字符串的路由参数
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-10 13:56:13
 * @param {Array<string | number>} params 参数数组
 * @return {string}
 */
export function encodeRouterParams(params) {
    return btoa(params.join('_'));
}
/**
 * 将 base64 字符串路由参数解码成参数数组
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-10 13:57:52
 * @param {string} param
 * @return {string[]}
 */
export async function decodeRouterParams(param) {
    try {
        const ret = atob(param).split('_');
        return Promise.resolve(ret);
    }
    catch {
        return Promise.resolve([]);
    }
}
/**
 * ```
 * 简单的号码格式化
 * 11位手机号 -> 344格式
 * 10位座机号 -> 343格式
 * 其它不做处理
 * ```
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-27 10:04:31
 */
export const mobileFormat = (mobile) => {
    // ***-****-****
    if (/\d{11}/.test(mobile)) {
        return mobile.replace(/(\d{3}(?=\d{8})|\d{4})/g, '$1 ');
    }
    // 400-****-***
    if (/\d{10}/.test(mobile)) {
        return mobile.replace(/(\d{4}(?=\d{3}$)|\d{3}(?=\d{7}$))/g, '$1 ');
    }
    return mobile;
};
//# sourceMappingURL=func.js.map